import { http } from "./http";
import type { ReportAudience, ReportDefinition, ResultColumn, ResultCell, SectionResult } from "./portalReportsApi";

export interface AdminReportListItem {
  portalReportId: number;
  name: string;
  menuLabel: string;
  description: string;
  enabled: boolean;
  audience: ReportAudience;
  sortOrder: number;
  sectionCount: number;
  assignedAll: boolean;
  assignedCustomers: number;
  modifiedUtc: string;
}

export interface AdminReport {
  portalReportId: number;
  name: string;
  menuLabel: string;
  description: string;
  enabled: boolean;
  audience: ReportAudience;
  sortOrder: number;
  definition: ReportDefinition;
  assignedAll: boolean;
  customerIds: number[];
  modifiedUtc: string;
}

export interface UpdateReportBody {
  name: string;
  menuLabel: string;
  description: string;
  enabled: boolean;
  audience: ReportAudience;
  sortOrder: number;
  definition: ReportDefinition;
}

export interface ProcedureInfo {
  name: string;
  modifyDate: string;
}

export interface ProcedureParameter {
  name: string;
  sqlType: string;
  maxLength: number;
  precision: number;
  scale: number;
  hasDefault: boolean;
  isOutput: boolean;
  position: number;
}

export interface ProcedureColumn {
  name: string;
  sqlType: string;
  isNullable: boolean;
  position: number;
}

export interface ProcedureDetail extends ProcedureInfo {
  parameters: ProcedureParameter[];
  /** Empty when SQL Server cannot describe the result set (use a test run). */
  columns: ProcedureColumn[];
}

export interface TestRunResult {
  columns: ResultColumn[];
  rows: ResultCell[][];
  truncated: boolean;
  error?: string | null;
  timeZone?: string | null;
}

const base = "/api/admin/reports";

export const portalReportsAdminApi = {
  list: () => http<AdminReportListItem[]>(base),
  get: (name: string) => http<AdminReport>(`${base}/${encodeURIComponent(name)}`),
  create: (body: { name: string; menuLabel: string; audience: ReportAudience }) =>
    http<{ portalReportId: number; name: string }>(base, { method: "POST", body: JSON.stringify(body) }),
  update: (name: string, body: UpdateReportBody) =>
    http<{ ok: boolean; name: string; warnings: string[] }>(`${base}/${encodeURIComponent(name)}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (name: string) => http<{ ok: boolean }>(`${base}/${encodeURIComponent(name)}`, { method: "DELETE" }),
  setAssignments: (name: string, body: { allCustomers: boolean; customerIds: number[] }) =>
    http<{ ok: boolean }>(`${base}/${encodeURIComponent(name)}/assignments`, { method: "PUT", body: JSON.stringify(body) }),
  preview: (name: string, body: { inputs: Record<string, string>; customerId?: number | null; managerId?: number | null; definition?: ReportDefinition }) =>
    http<{ sections: SectionResult[]; timeZone?: string | null }>(`${base}/${encodeURIComponent(name)}/preview`, { method: "POST", body: JSON.stringify(body) }),

  procedures: () => http<ProcedureInfo[]>(`${base}/procedures`),
  procedure: (name: string) => http<ProcedureDetail>(`${base}/procedures/${encodeURIComponent(name)}`),
  procedureSource: (name: string) =>
    http<{ name: string; modifyDate: string; definition: string }>(`${base}/procedures/${encodeURIComponent(name)}/source`),
  testRun: (name: string, body: { values: Record<string, string>; customerId?: number | null; managerId?: number | null }) =>
    http<TestRunResult>(`${base}/procedures/${encodeURIComponent(name)}/test`, { method: "POST", body: JSON.stringify(body) }),
};
